import {
  FormProvider,
  SubmitHandler,
  useForm,
  useFormContext,
} from "react-hook-form";
import {
  DriverLogDetailedView,
  EventApiFactory,
} from "../../../../../common/data/service-proxies";
import { CompactFormToolbar } from "../../../../common/form/CompactFormToolbar";
import { CompactSaveButton } from "../../../../common/form/CompactSaveButton";
import { useNotify } from "react-admin";
import { useAxiosInstance } from "../../../../../common/hooks/useAxiosInstance";
import { useCallback } from "react";
import { handleAxiosError } from "../../../../../common/data/axiosErrorHandler";
import { Card, CardContent } from "@material-ui/core";
import classnames from "classnames";
import { HookFormSelectInput } from "../../../../common/form/hook-form/inputs/HookFormSelectInput";

interface Props {
  record: DriverLogDetailedView;
  onSuccess: () => void;
  onFailed?: () => void;
}

type FormValues = {
  fixSequenceNumbers?: boolean;
  fixEngineHours?: boolean;
};

const DefaultCompactFormToolbar = () => {
  const ctx = useFormContext();
  const {
    formState: { isSubmitting },
  } = ctx;

  return (
    <CompactFormToolbar>
      <CompactSaveButton saving={isSubmitting} disabled={isSubmitting} />
    </CompactFormToolbar>
  );
};

export const FixEventsForm = ({
  record,
  onSuccess,
  onFailed,
  ...rest
}: Props) => {
  const { startOfDay, driverId } = record;
  const notify = useNotify();
  const axios = useAxiosInstance();

  const onSubmit = useCallback(
    (data: FormValues) => {
      if (!axios) {
        return;
      }

      if (!startOfDay || !driverId) {
        return;
      }

      return EventApiFactory(undefined, axios.defaults.baseURL, axios)
        .apiAppEventFixeventsPost({
          ...data,
          selectedDate: startOfDay,
          driverId,
        })
        .then(() => {
          onSuccess();
        })
        .catch((e) => {
          if (onFailed) {
            onFailed();
          }

          console.error(e);
          notify(handleAxiosError(e), { type: "error" });
        });
    },
    [axios, notify, onSuccess, onFailed, driverId, startOfDay]
  );

  const formContext = useForm<FormValues>({
    mode: "onChange",
  });

  const { handleSubmit, control } = formContext;

  const handleSubmitInternal: SubmitHandler<FormValues> = (data) => {
    return Promise.resolve(onSubmit(data)).catch((e: Error) => {
      notify(e.message ?? "An unexpected error has occurred", "error");
    });
  };

  return (
    <CardContent>
      <FormProvider {...formContext}>
        <form
          className={classnames("simple-form", "form-create-edit-drawer")}
          {...sanitizeRestProps(rest)}
          onSubmit={handleSubmit(handleSubmitInternal, (err) =>
            console.error(err)
          )}>
          <Card>
            <CardContent>
              <div className="ra-input">
                <HookFormSelectInput<FormValues>
                  control={control}
                  choices={[
                    { id: false, name: "No" },
                    { id: true, name: "Yes" },
                  ]}
                  name="fixSequenceNumbers"
                  label="Fix sequence numbers"
                  variant="outlined"
                  defaultValue={false}
                />
              </div>
              <div className="ra-input">
                <HookFormSelectInput<FormValues>
                  control={control}
                  choices={[
                    { id: false, name: "No" },
                    { id: true, name: "Yes" },
                  ]}
                  name="fixEngineHours"
                  label="Fix engine hours"
                  variant="outlined"
                  defaultValue={false}
                />
              </div>
            </CardContent>
          </Card>
          <DefaultCompactFormToolbar />
        </form>
      </FormProvider>
    </CardContent>
  );
};

const sanitizeRestProps = ({
  active,
  dirty,
  dirtyFields,
  dirtyFieldsSinceLastSubmit,
  dirtySinceLastSubmit,
  error,
  errors,
  form,
  hasSubmitErrors,
  hasValidationErrors,
  initialValues,
  modified = null,
  modifiedSinceLastSubmit,
  save = null,
  submitError,
  submitErrors,
  submitFailed,
  submitSucceeded,
  submitting,
  touched = null,
  valid,
  values,
  visited = null,
  __versions = null,
  ...props
}: any) => props;
